// Per-controller visuals: a faded laser line parented to the controller
// (length set per-frame from the pick distance) and a 1-cell face
// highlight parented to the scene (positioned in world space each frame
// from the pick target).
//
// Cursor color is per-controller — green for draw, red for erase — so
// the user can tell at a glance which trigger does what. This is a
// phase-2 simplification; phase 5 wires the dial-driven tool state
// back in and the cursor color follows the active tool.

import * as THREE from "three";
import { Line2 } from "three/addons/lines/Line2.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";
import { CELL_SIZE } from "../model/voxel-grid";
import type { PickTarget } from "../interaction/pick";
import { faceNormal } from "../model/voxel-pick";

// Fixed-length laser stub. LASER_OFFSET is measured from the controller's
// WebXR target-ray origin, which sits somewhere inside the visible
// controller mesh (the mesh itself is parented to grip space, not target-
// ray). So the offset bakes in an empirical "depth of the controller
// front past target-ray origin" (~3in on Quest Touch) plus the 2in gap
// the design calls for. Tweak if the gap looks off on your hardware.
const LASER_OFFSET = 5 * 0.0254;
const LASER_LENGTH = 14 * 0.0254;
// Width is in screen pixels (LineMaterial uses GL_LINES alternative that
// rasterizes triangle strips, so this is honored everywhere — unlike the
// plain THREE.Line `linewidth` which is clamped to 1px in WebGL).
const LASER_WIDTH_PX = 6;
// Fade the first and last inch of the laser so the ends soften into the
// scene instead of butting up against the controller / the cursor.
const LASER_FADE_INCHES = 1;
const LASER_LENGTH_INCHES = 14;
// LineMaterial's body-space vUv.y runs from -1 at the start to +1 at the
// end. (1 - |vUv.y|) is the distance to the nearest end measured in
// half-line units. Multiply by (length / 2) / fade to get 1.0 right at
// the inner edge of the fade region; clamp to clip beyond.
const LASER_FADE_VUV_MULTIPLIER =
  LASER_LENGTH_INCHES / (2 * LASER_FADE_INCHES);

const DRAW_COLOR = 0x6ec0a3; // teal-green
const ERASE_COLOR = 0xe57b7b; // muted red
const MARQUEE_COLOR = 0xa970d4; // violet — matches the marquee rect
const MOVE_COLOR = 0xf4b400; // amber — matches the selection outline

// Face cursor is always white — it indicates the cell the laser would
// act on, independent of which tool is active. The laser line carries
// the tool tint instead.
const FACE_CURSOR_COLOR = 0xffffff;

// Off-canvas state: the laser desaturates to gray and a circular disc
// cursor floats at the world-space hit point (grab bar, exit button, or
// the laser's own tip in empty space). The disc matches the grab bar's
// off-white fill so the two read as a system.
const OFF_CANVAS_LASER_COLOR = 0xffffff;
const OFF_CANVAS_CURSOR_COLOR = 0xf5f3ec;
// A hair darker than the cursor fill — a subtle outline that makes the
// dot read against light passthrough scenes without competing with it.
const OFF_CANVAS_CURSOR_BORDER_COLOR = 0xd8d6cf;
// Cursor's apparent angular size as a fraction of its distance from the
// user's head (`radius / distance`). Picked so the cursor at typical VR
// arm's-length matches the previous fixed 1 cm radius. The mesh's base
// radius is 1 m and we scale it per frame to hit `distance × ratio`.
const OFF_CANVAS_CURSOR_APPARENT_RATIO = 0.0075;
const OFF_CANVAS_CURSOR_BASE_RADIUS = 1.0;

type RayTint = "draw" | "erase" | "marquee" | "move";

function tintHex(tint: RayTint): number {
  switch (tint) {
    case "draw":
      return DRAW_COLOR;
    case "erase":
      return ERASE_COLOR;
    case "marquee":
      return MARQUEE_COLOR;
    case "move":
      return MOVE_COLOR;
  }
}

export interface ControllerRay {
  // Laser line — parented to the controller, extends along its -Z axis.
  // Uses fat-line rendering (Line2) so width is actually honored.
  line: Line2;
  // Face highlight quad — parented to the scene, positioned in world
  // space each frame so it sits flush on the picked face.
  cursor: THREE.Mesh;
  // Disc cursor shown when the laser is pointing off-canvas (at the
  // grab bar, exit button, or into empty space). Parented to the scene
  // (world space) so it doesn't scale with the cube. Billboards toward
  // the camera each frame it's visible.
  offCanvasCursor: THREE.Mesh;
  // Remembered tool tint so on-canvas frames can restore the colored
  // laser after a transient off-canvas frame set it to gray.
  tintHex: number;
}

export function createControllerRay(
  controller: THREE.Object3D,
  // Parent for the face cursor. Sits in grid-local coordinates so it
  // scales naturally with the cube — caller passes `gridGroup`. The
  // laser line stays parented to the controller (length is in world
  // units regardless of cube scale).
  cursorParent: THREE.Object3D,
  // World-space parent for the off-canvas disc cursor — pass the scene
  // so the disc keeps a constant on-screen size and doesn't follow
  // gridGroup's pinch-scale.
  worldParent: THREE.Object3D,
  tint: RayTint,
): ControllerRay {
  const color = tintHex(tint);

  const lineGeom = new LineGeometry();
  lineGeom.setPositions([
    0, 0, -LASER_OFFSET,
    0, 0, -(LASER_OFFSET + LASER_LENGTH),
  ]);
  const lineMat = new LineMaterial({
    color,
    linewidth: LASER_WIDTH_PX,
    transparent: true,
    opacity: 0.7,
  });
  // Resolution is the screen size the pixel-width is measured against.
  // Set once from window; mild drift on resize is acceptable for a
  // fixed-thickness stub.
  lineMat.resolution.set(window.innerWidth, window.innerHeight);
  // Multiply the final alpha by a 0→1 fade factor so the first and last
  // inch of the laser body soften out instead of cliff-edging.
  lineMat.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      "gl_FragColor = vec4( diffuseColor.rgb, alpha );",
      `float laserFade = clamp((1.0 - abs(vUv.y)) * ${LASER_FADE_VUV_MULTIPLIER.toFixed(4)}, 0.0, 1.0);
       gl_FragColor = vec4( diffuseColor.rgb, alpha * laserFade );`,
    );
  };
  const line = new Line2(lineGeom, lineMat);
  controller.add(line);

  const cursor = new THREE.Mesh(
    new THREE.PlaneGeometry(CELL_SIZE, CELL_SIZE),
    new THREE.MeshBasicMaterial({
      color: FACE_CURSOR_COLOR,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    }),
  );
  cursor.renderOrder = 999;
  cursor.visible = false;
  cursorParent.add(cursor);

  const offCanvasCursor = new THREE.Mesh(
    new THREE.CircleGeometry(OFF_CANVAS_CURSOR_BASE_RADIUS, 32),
    new THREE.MeshBasicMaterial({
      color: OFF_CANVAS_CURSOR_COLOR,
      transparent: true,
      opacity: 0.8,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
  offCanvasCursor.renderOrder = 999;
  offCanvasCursor.visible = false;
  worldParent.add(offCanvasCursor);

  // Outline — sampled circle at the base radius, parented to the disc
  // so it inherits both the per-frame scale and visibility toggle.
  const borderSegs = 48;
  const borderPts: THREE.Vector3[] = [];
  for (let s = 0; s < borderSegs; s++) {
    const a = (s / borderSegs) * Math.PI * 2;
    borderPts.push(
      new THREE.Vector3(
        Math.cos(a) * OFF_CANVAS_CURSOR_BASE_RADIUS,
        Math.sin(a) * OFF_CANVAS_CURSOR_BASE_RADIUS,
        0.001,
      ),
    );
  }
  const offCanvasCursorBorder = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints(borderPts),
    new THREE.LineBasicMaterial({
      color: OFF_CANVAS_CURSOR_BORDER_COLOR,
      transparent: true,
      opacity: 0.8,
      depthTest: false,
      depthWrite: false,
    }),
  );
  offCanvasCursorBorder.renderOrder = 1000;
  offCanvasCursor.add(offCanvasCursorBorder);

  return { line, cursor, offCanvasCursor, tintHex: color };
}

const _cursorQuat = new THREE.Quaternion();
const _planeNormalDefault = new THREE.Vector3(0, 0, 1);
const _planeNormalTmp = new THREE.Vector3();

// Position + orient the cursor flush on a face of cell (ix,iy,iz) whose
// outward normal is `normal` in grid-local space. Cursor lives in the
// gridGroup's local frame (it's parented to gridGroup), so both the
// position and the orientation stay in that frame — no world-space
// transforms needed, and the cursor inherits the grid's scale.
function placeCursorOnFace(
  cursor: THREE.Mesh,
  cell: { ix: number; iy: number; iz: number },
  normal: [number, number, number],
): void {
  const [nx, ny, nz] = normal;
  cursor.position.set(
    (cell.ix + 0.5) * CELL_SIZE + (nx * CELL_SIZE) / 2,
    (cell.iy + 0.5) * CELL_SIZE + (ny * CELL_SIZE) / 2,
    (cell.iz + 0.5) * CELL_SIZE + (nz * CELL_SIZE) / 2,
  );
  // PlaneGeometry default normal is +Z; rotate onto the face normal.
  _planeNormalTmp.set(nx, ny, nz);
  _cursorQuat.setFromUnitVectors(_planeNormalDefault, _planeNormalTmp);
  cursor.quaternion.copy(_cursorQuat);
}

// Update one controller's visuals for an in-canvas pick (voxel or
// surface). Restores the tool-tinted laser, hides the off-canvas disc,
// and places the face-cell cursor on the hit face. The caller handles
// the off-canvas branches via setControllerRayOffCanvas /
// hideControllerRay.
//
// `laserDistanceOverride`, when supplied, replaces pick.distance for
// the laser-length calculation only (the cursor still sits on the
// pick's face). Used by callers that want to cap the laser at a
// closer obstacle than pick reported — e.g. a visible wall the laser
// would otherwise extend past.
export function updateControllerRayVisuals(
  ray: ControllerRay,
  pick: Exclude<PickTarget, null>,
  laserDistanceOverride?: number,
  maxLength: number = LASER_LENGTH,
): void {
  setLaserGeometry(ray, laserDistanceOverride ?? pick.distance, maxLength);
  setLaserColor(ray, ray.tintHex);
  ray.offCanvasCursor.visible = false;
  ray.cursor.visible = true;
  if (pick.kind === "voxel") {
    placeCursorOnFace(
      ray.cursor,
      { ix: pick.ix, iy: pick.iy, iz: pick.iz },
      faceNormal(pick.face),
    );
  } else {
    placeCursorOnFace(
      ray.cursor,
      { ix: pick.ix, iy: pick.iy, iz: pick.iz },
      pick.normal,
    );
  }
}

// Off-canvas hit (grab bar, exit button, or any other floating-UI
// target whose world position is known). Grays the laser and parks the
// disc cursor at `worldPos`, billboarded toward the camera.
export function setControllerRayOffCanvas(
  ray: ControllerRay,
  worldPos: THREE.Vector3,
  cameraWorldPos: THREE.Vector3,
  hitDistance: number,
  maxLength: number = LASER_LENGTH,
): void {
  setLaserGeometry(ray, hitDistance, maxLength);
  setLaserColor(ray, OFF_CANVAS_LASER_COLOR);
  ray.cursor.visible = false;
  placeOffCanvasCursor(ray, worldPos, cameraWorldPos);
}

// Marquee-drag laser: stretch the laser to land exactly on `tipDistance`
// (the box's current dragged corner) with NO fixed-stub cap, so the line
// stays connected to the selection window — never overshooting or falling
// short. Tinted violet to match the selection rectangle; no face cursor
// or disc.
export function setControllerRayMarquee(
  ray: ControllerRay,
  tipDistance: number,
): void {
  setLaserGeometry(ray, tipDistance, Number.POSITIVE_INFINITY);
  setLaserColor(ray, MARQUEE_COLOR);
  ray.cursor.visible = false;
  ray.offCanvasCursor.visible = false;
}

function placeOffCanvasCursor(
  ray: ControllerRay,
  worldPos: THREE.Vector3,
  cameraWorldPos: THREE.Vector3,
): void {
  ray.offCanvasCursor.position.copy(worldPos);
  ray.offCanvasCursor.lookAt(cameraWorldPos);
  // Scale to a constant angular size: target world radius scales
  // linearly with distance from the head.
  const distance = worldPos.distanceTo(cameraWorldPos);
  const targetRadius = distance * OFF_CANVAS_CURSOR_APPARENT_RATIO;
  ray.offCanvasCursor.scale.setScalar(
    targetRadius / OFF_CANVAS_CURSOR_BASE_RADIUS,
  );
  ray.offCanvasCursor.visible = true;
}

function setLaserColor(ray: ControllerRay, hex: number): void {
  (ray.line.material as LineMaterial).color.setHex(hex);
}

// Truncate the laser so its tip never extends past `hitDistance` (in
// world units along controller −Z). Caller passes the distance from the
// controller's target-ray origin to wherever the cursor is sitting.
// When the hit is so close it'd fall inside the controller-front offset,
// the laser hides entirely.
// The SELECT-tool ("marquee") laser is ~25% shorter than the standard
// stub, so the selection window sits closer to the hand.
export const SELECT_LASER_LENGTH = LASER_LENGTH * 0.75;

// World-space distance from the controller's target-ray origin to the
// VISIBLE tip of the laser stub for a given hit distance. The laser is a
// fixed-length stub — it doesn't reach distant targets — so anything that
// wants to sit "at the tip of the laser" must use this, not the raw pick
// distance (which can be metres away while the visible laser ends ~19in
// out). Mirrors the clamp in setLaserGeometry; pass the matching
// `maxLength` (e.g. SELECT_LASER_LENGTH) so the tip lines up.
export function laserTipDistance(
  hitDistance: number,
  maxLength: number = LASER_LENGTH,
): number {
  return Math.min(hitDistance, LASER_OFFSET + maxLength);
}

function setLaserGeometry(
  ray: ControllerRay,
  hitDistance: number,
  maxLength: number = LASER_LENGTH,
): void {
  if (hitDistance <= LASER_OFFSET) {
    ray.line.visible = false;
    return;
  }
  const length = Math.min(maxLength, hitDistance - LASER_OFFSET);
  ray.line.geometry.setPositions([
    0, 0, -LASER_OFFSET,
    0, 0, -(LASER_OFFSET + length),
  ]);
  ray.line.visible = true;
}

// Retint an existing ray + face cursor (so tool changes via the dial
// or toolkit don't require rebuilding the meshes). Stores the new tint
// on the ray so the next on-canvas frame can restore it after any
// off-canvas gray-out.
export function setControllerRayTint(
  ray: ControllerRay,
  tint: RayTint,
): void {
  const color = tintHex(tint);
  ray.tintHex = color;
  setLaserColor(ray, color);
}

export function hideControllerRay(ray: ControllerRay): void {
  ray.line.visible = false;
  ray.cursor.visible = false;
  ray.offCanvasCursor.visible = false;
}

// Aim the laser at a summoned UI panel (palette / toolkit). Laser stays
// tool-tinted; the off-canvas disc cursor parks on the panel surface at
// the hit point, billboarded toward the camera, so the user gets a
// consistent cursor whether they're on a panel, the grab bar, or empty
// space. The panel still paints its own inset button highlight on top
// of the texture; the disc reads as the pointer dot on top of that.
export function aimControllerRayAtPanel(
  ray: ControllerRay,
  worldPos: THREE.Vector3,
  cameraWorldPos: THREE.Vector3,
  hitDistance: number,
): void {
  setLaserGeometry(ray, hitDistance);
  setLaserColor(ray, ray.tintHex);
  ray.cursor.visible = false;
  placeOffCanvasCursor(ray, worldPos, cameraWorldPos);
}
